import React, { useMemo } from "react";
import styled from "styled-components";

const BubblesContainer = styled.div`
  display: flex;
  justify-content: space-around;
  flex-wrap: wrap;
  gap: 15px;
  margin-bottom: 30px;
`;

const Bubble = styled.div`
  width: 150px;
  height: 150px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: transform 0.3s, box-shadow 0.3s;
  background-color: ${(props) => (props.selected ? "#6ed216" : "#f5f5f5")};
  color: ${(props) => (props.selected ? "white" : "#333")};
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const BubbleValue = styled.div`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 5px;
`;

const BubbleLabel = styled.div`
  font-size: 14px;
  text-transform: uppercase;
`;

function KPIBubbles({ data, onKPISelect, selectedKPI }) {
  // Calculer les KPIs avec useMemo pour optimiser les performances
  // Placer le hook useMemo avant toute condition de retour
  const kpiValues = useMemo(() => {
    // Si les données sont invalides, retourner des valeurs par défaut
    if (!data || !Array.isArray(data) || data.length === 0) {
      return {
        impressions: 0,
        clicks: 0,
        ctr: 0,
        budget: 0,
        viewability: 0,
      };
    }

    // Calculer les totaux
    let totalImpressions = 0;
    let totalClicks = 0;
    let totalBudget = 0;

    // Pour la viewability, utiliser une moyenne pondérée
    let weightedViewability = 0;
    let viewabilityWeight = 0;

    data.forEach((item) => {
      // Accumuler les valeurs de base
      const impressions = Number(item.impressions || item.imps || 0);
      const clicks = Number(item.clicks || 0);
      const budget = Number(item.budget || item.cost || 0);
      const viewableImpressions = Number(item.viewableImpressions || 0);

      totalImpressions += impressions;
      totalClicks += clicks;
      totalBudget += budget;

      // Pour la viewability, essayer plusieurs approches
      // 1. Utiliser directement la valeur de viewability si disponible
      const viewability = Number(item.viewability || 0);
      
      if (viewability > 0 && impressions > 0) {
        weightedViewability += viewability * impressions;
        viewabilityWeight += impressions;
      } 
      // 2. Sinon, utiliser les impressions visibles si disponibles
      else if (viewableImpressions > 0 && impressions > 0) {
        // Calculer la viewability pour cette ligne
        const lineViewability = (viewableImpressions / impressions) * 100;
        weightedViewability += lineViewability * impressions;
        viewabilityWeight += impressions;
      }
    });

    // Calculer les métriques dérivées
    const ctr =
      totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0;

    // Calculer la viewability moyenne pondérée
    const viewability =
      viewabilityWeight > 0 ? weightedViewability / viewabilityWeight : 0;

    return {
      impressions: totalImpressions,
      clicks: totalClicks,
      ctr,
      budget: totalBudget,
      viewability,
    };
  }, [data]); // Recalculer uniquement lorsque les données changent

  // Vérifier si les données sont valides après avoir défini tous les hooks
  if (!data || !Array.isArray(data) || data.length === 0) {
    return null;
  }

  // Formater les valeurs pour l'affichage
  const formatKPI = (kpi, value) => {
    switch (kpi) {
      case "impressions":
        return value.toLocaleString("fr-FR");
      case "clicks":
        return value.toLocaleString("fr-FR");
      case "ctr":
        return (
          value.toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "%"
        );
      case "budget":
        return value.toLocaleString("fr-FR", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      case "viewability":
        // S'assurer que la valeur est un nombre
        const viewabilityValue = Number(value) || 0;
        return (
          viewabilityValue.toLocaleString("fr-FR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }) + "%"
        );
      default:
        return value.toString();
    }
  };

  // Définir les KPIs à afficher
  const kpis = [
    {
      id: "impressions",
      label: "Impressions",
      value: kpiValues.impressions,
      formattedValue: formatKPI("impressions", kpiValues.impressions),
    },
    {
      id: "clicks",
      label: "Clics",
      value: kpiValues.clicks,
      formattedValue: formatKPI("clicks", kpiValues.clicks),
    },
    {
      id: "ctr",
      label: "CTR",
      value: kpiValues.ctr,
      formattedValue: formatKPI("ctr", kpiValues.ctr),
    },
    {
      id: "budget",
      label: "Budget",
      value: kpiValues.budget,
      formattedValue: formatKPI("budget", kpiValues.budget),
    },
    {
      id: "viewability",
      label: "Visibilité",
      value: kpiValues.viewability,
      formattedValue: formatKPI("viewability", kpiValues.viewability),
    },
  ];

  return (
    <BubblesContainer>
      {kpis.map((kpi) => (
        <Bubble
          key={kpi.id}
          selected={selectedKPI === kpi.id}
          onClick={() => onKPISelect(kpi.id)}
        >
          <BubbleValue>{kpi.formattedValue}</BubbleValue>
          <BubbleLabel>{kpi.label}</BubbleLabel>
        </Bubble>
      ))}
    </BubblesContainer>
  );
}

export default KPIBubbles;
